import {
  CreditCardIcon,
  EmailIcon,
  EmployeeIcon,
  GKButton,
  GKDatePicker,
  GKModal,
  GKTextField,
  Grid2,
  Phone1Icon,
  PhoneInput,
  RupeeIcon,
  Typography,
  UserIcon,
} from "components";
import dayjs from "dayjs";
import useAddEmployee from "hooks/useAddEmployee";
import { useState } from "react";
import { Controller } from "react-hook-form";
import { GKModalProps } from "utils/commonInterface";

const AddEmployee = ({
  open,
  setOpen,
  selectedId,
  setSelectedId,
}: GKModalProps & { selectedId: any; setSelectedId: any }) => {
  const { isSubmitting, form, onSubmit } = useAddEmployee({
    setOpen,
    selectedId,
    setSelectedId,
  });

  const [hasError, setHasError] = useState(false);

  form.watch();

  return (
    <GKModal
      modalTitle="Add New Admin"
      open={open}
      setOpen={setOpen}
      onCloseModal={() => {
        form.reset();
        setSelectedId(null);
        setOpen(false);
      }}
      footer={
        <Grid2 size={12} textAlign={"right"}>
          <GKButton
            type="submit"
            variant="contained"
            size="medium"
            form="employee-form"
            disabled={hasError}
            loading={isSubmitting}
          >
            {"Submit"}
          </GKButton>
        </Grid2>
      }
    >
      <form id="employee-form" onSubmit={onSubmit}>
        <Grid2 container spacing={2}>
          <Grid2 size={12} mt={1}>
            <Typography fontWeight={600} fontSize={16}>
              Employee Details
            </Typography>
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="user.personalDetails.name"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  inputLabel="Name"
                  requiredField
                  error={!!error}
                  helperText={error?.message}
                  slotProps={{
                    input: {
                      endAdornment: <UserIcon width={20} height={20} />,
                    },
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="user.personalDetails.email"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  requiredField
                  inputLabel="Email"
                  placeholder="Email"
                  error={!!error}
                  helperText={error?.message}
                  slotProps={{
                    input: {
                      endAdornment: <EmailIcon width={20} height={20} />,
                    },
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="user.phone"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <PhoneInput
                  {...field}
                  inputLabel="Phone Number"
                  requiredField
                  onSelectChange={(event: any) => {
                    form.setValue("user.countryCode", event);
                  }}
                  onTextChange={(event: any) => {
                    field.onChange(event);
                  }}
                  onTextValue={field.value}
                  onSelectValue={form.getValues("user.countryCode")}
                  error={!!error || hasError}
                  helperText={error?.message}
                  hasError={hasError}
                  setHasError={setHasError}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="user.personalDetails.dateOfBirth"
              control={form.control}
              render={({ field }) => (
                <GKDatePicker
                  {...field}
                  inputLabel="Date of Birth"
                  value={dayjs(field.value)}
                  onChange={(value) => {
                    field.onChange(dayjs(value).format("YYYY-MM-DD"));
                  }}
                  minDate={dayjs("01-01-1901")}
                  slotProps={{
                    textField: {
                      error: false,
                      helperText: false,
                    },
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="user.personalDetails.aadhar"
              control={form.control}
              render={({ field }) => (
                <GKTextField
                  {...field}
                  type="number"
                  inputLabel="Aadhar Card"
                  slotProps={{
                    input: {
                      endAdornment: <CreditCardIcon width={20} height={20} />,
                    },
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={12} mt={1}>
            <Typography fontWeight={600} fontSize={16}>
              Job Details
            </Typography>
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="position"
              control={form.control}
              render={({ field }) => (
                <GKTextField
                  {...field}
                  inputLabel="Position"
                  slotProps={{
                    input: {
                      endAdornment: <EmployeeIcon width={20} height={20} />,
                    },
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="salary"
              control={form.control}
              render={({ field }) => (
                <GKTextField
                  {...field}
                  type="number"
                  inputLabel="Salary"
                  slotProps={{
                    input: {
                      endAdornment: <RupeeIcon width={20} height={20} />,
                    },
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={12} mt={1}>
            <Typography fontWeight={600} fontSize={16}>
              Emergency contact details{" "}
            </Typography>
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="user.personalDetails.emergencyContact"
              control={form.control}
              render={({ field }) => (
                <GKTextField
                  {...field}
                  inputLabel="Emergency Contact Name"
                  slotProps={{
                    input: {
                      endAdornment: <UserIcon width={20} height={20} />,
                    },
                  }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="user.personalDetails.emergencyContactNum"
              control={form.control}
              rules={{
                minLength: {
                  value: 10,
                  message: "Minimum length is 10 characters",
                },
                maxLength: {
                  value: 10,
                  message: "Maximum length is 10 characters",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKTextField
                  {...field}
                  type="number"
                  inputLabel="Emergency Contact Number"
                  error={!!error}
                  helperText={error?.message}
                  slotProps={{
                    input: {
                      endAdornment: <Phone1Icon width={20} height={20} />,
                    },
                  }}
                />
              )}
            />
          </Grid2>
        </Grid2>
      </form>
    </GKModal>
  );
};

export default AddEmployee;
